/** @type {string} */
export const CUSTOMER_NEWSLETTER_SESSION_KEY = 'customer_newsletter_form_id';

/** @type {string} */
export const POPUP_SUPPRESS_COOKIE = 'newsletter_popup_suppressed';

/** @type {string} */
export const CUSTOMER_NEWSLETTER_SUBSCRIBED_COOKIE = 'customer_newsletter_subscribed';

/** @type {boolean} */
let responseApplied = false;

/**
 * @returns {boolean}
 */
function hasCustomerFormRedirect() {
  const { search } = window.location;
  return search.includes('customer_posted=true') || search.includes('form_type=customer');
}

/**
 * @returns {string | null}
 */
function getSubmittedFormId() {
  const fromStorage = sessionStorage.getItem(CUSTOMER_NEWSLETTER_SESSION_KEY);
  if (fromStorage) return fromStorage;

  if (!hasCustomerFormRedirect()) return null;

  const hashId = window.location.hash.replace(/^#/, '');
  if (hashId.startsWith('FooterUtilities-') || hashId.startsWith('NewsletterPopup-')) {
    return hashId;
  }

  const footerForm = document.querySelector('[data-customer-form-id^="FooterUtilities-"]');
  if (footerForm instanceof HTMLElement && footerForm.dataset.customerFormId) {
    const footerResponded =
      footerForm.hasAttribute('data-customer-form-posted-success') ||
      footerForm.hasAttribute('data-customer-form-already-subscribed');
    if (footerResponded) {
      return footerForm.dataset.customerFormId;
    }
  }

  const popupResponse = document.querySelector(
    'newsletter-popup-component[data-customer-form-posted-success], newsletter-popup-component[data-customer-form-already-subscribed]'
  );
  if (popupResponse instanceof HTMLElement && popupResponse.dataset.customerFormId) {
    return popupResponse.dataset.customerFormId;
  }

  if (footerForm instanceof HTMLElement && footerForm.dataset.customerFormId) {
    return footerForm.dataset.customerFormId;
  }

  return null;
}

/**
 * @param {string} submittedFormId
 * @returns {HTMLElement | null}
 */
function getFormResponseElement(submittedFormId) {
  if (submittedFormId.startsWith('FooterUtilities-')) {
    const container = document.querySelector(`[data-customer-form-id="${submittedFormId}"]`);
    return container instanceof HTMLElement ? container : null;
  }

  if (submittedFormId.startsWith('NewsletterPopup-')) {
    const popup = document.querySelector('newsletter-popup-component');
    return popup instanceof HTMLElement ? popup : null;
  }

  return null;
}

/**
 * @returns {boolean}
 */
function isSuccessfulCustomerPost() {
  const submittedFormId = getSubmittedFormId();
  if (!submittedFormId) return false;

  return getFooterSuccessVariant(submittedFormId) === 'thanks';
}

/**
 * @param {string} submittedFormId
 * @returns {boolean}
 */
function hasFormResponseErrors(submittedFormId) {
  const element = getFormResponseElement(submittedFormId);
  return element?.hasAttribute('data-customer-form-has-errors') ?? false;
}

/**
 * @param {string} submittedFormId
 * @returns {boolean}
 */
function isAlreadySubscribedFormResponse(submittedFormId) {
  return getFooterSuccessVariant(submittedFormId) === 'subscribed';
}

/**
 * @returns {boolean}
 */
export function isCustomerFormResponse() {
  if (hasCustomerFormRedirect()) return true;

  return (
    sessionStorage.getItem(CUSTOMER_NEWSLETTER_SESSION_KEY) != null &&
    document.querySelector(
      '[data-customer-form-posted-success], [data-customer-form-already-subscribed]'
    ) != null
  );
}

/**
 * @param {number} days
 */
export function suppressNewsletterPopup(days = 7) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${POPUP_SUPPRESS_COOKIE}=1; expires=${expires}; path=/; SameSite=Lax`;
}

/**
 * @returns {boolean}
 */
export function isCustomerNewsletterSubscribed() {
  return document.cookie
    .split('; ')
    .some((row) => row.startsWith(`${CUSTOMER_NEWSLETTER_SUBSCRIBED_COOKIE}=1`));
}

/**
 * @param {number} days
 */
export function markCustomerNewsletterSubscribed(days = 7) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${CUSTOMER_NEWSLETTER_SUBSCRIBED_COOKIE}=1; expires=${expires}; path=/; SameSite=Lax`;
}

/**
 * Swaps the popup content from the form to the success message without
 * triggering any auto-open behavior.
 *
 * @param {HTMLElement} popup
 */
export function swapPopupToSuccess(popup) {
  const formState = popup.querySelector('[data-newsletter-popup-form]');
  const successState = popup.querySelector('[data-newsletter-popup-success]');

  if (formState instanceof HTMLElement) {
    formState.hidden = true;
  }

  if (successState instanceof HTMLElement) {
    successState.hidden = false;
  }
}

/**
 * @param {Element | null | undefined} popup
 */
export function syncPopupSubscriptionState(popup) {
  if (!(popup instanceof HTMLElement)) return;
  if (!isCustomerNewsletterSubscribed()) return;

  swapPopupToSuccess(popup);
}

/**
 * @returns {number}
 */
function getPopupCookieDays() {
  const popup = document.querySelector('newsletter-popup-component');
  return Number(popup?.dataset.cookieDays) || 7;
}

/**
 * @param {HTMLFormElement} form
 */
function rememberSubmittedForm(form) {
  const container =
    form.closest('[data-customer-form-id]') || form.querySelector('[data-customer-form-id]');
  const containerId =
    container instanceof HTMLElement ? container.dataset.customerFormId : undefined;
  const idInput = form.querySelector('input[name="contact[id]"]');
  const hiddenId = idInput instanceof HTMLInputElement ? idInput.value : undefined;
  const formId = containerId || hiddenId || form.id;

  if (formId) {
    sessionStorage.setItem(CUSTOMER_NEWSLETTER_SESSION_KEY, formId);
  }
}

/**
 * @param {HTMLElement} container
 * @param {'success' | 'error'} type
 * @param {{ focus?: boolean, scroll?: boolean }} [options]
 */
function revealFormFeedback(container, type, options = {}) {
  const { focus = true, scroll = true } = options;
  const selector =
    type === 'success' ? '[data-customer-form-success]' : '[data-customer-form-error]';
  const message = container.querySelector(selector);

  if (!(message instanceof HTMLElement)) return;

  message.hidden = false;

  if (focus) {
    message.focus({ preventScroll: true });
  }

  if (scroll) {
    container.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }
}

/**
 * @param {HTMLElement} container
 * @param {'thanks' | 'subscribed'} variant
 */
function setFooterSuccessText(container, variant) {
  const textEl = container.querySelector('[data-customer-form-success-text]');

  if (!(textEl instanceof HTMLElement)) return;

  const text =
    variant === 'thanks' ? container.dataset.successText : container.dataset.alreadySubscribedText;

  if (text) {
    textEl.textContent = text;
  }
}

/**
 * @param {HTMLElement} container
 * @param {'thanks' | 'subscribed'} variant
 * @param {{ focus?: boolean, scroll?: boolean }} [options]
 */
function revealFooterSuccess(container, variant, options = {}) {
  setFooterSuccessText(container, variant);
  revealFormFeedback(container, 'success', options);
}

/**
 * Shows footer success feedback on every footer newsletter form.
 *
 * @param {'thanks' | 'subscribed'} variant
 * @param {{ focus?: boolean, scroll?: boolean }} [options]
 */
function revealAllFooterSuccess(variant, options = {}) {
  document.querySelectorAll('[data-customer-form-id^="FooterUtilities-"]').forEach((container) => {
    if (container instanceof HTMLElement) {
      revealFooterSuccess(container, variant, options);
    }
  });
}

/**
 * @param {HTMLElement} popup
 */
export function showPopupSuccessState(popup) {
  popup.dataset.autoShow = 'success';
  swapPopupToSuccess(popup);
}

/**
 * @param {HTMLElement} popup
 */
function showPopupErrorState(popup) {
  popup.dataset.autoShow = 'error';

  const successState = popup.querySelector('[data-newsletter-popup-success]');
  if (successState instanceof HTMLElement) {
    successState.hidden = true;
  }

  const error = popup.querySelector('[data-customer-form-error]');
  if (error instanceof HTMLElement) {
    error.hidden = false;
  }
}

/**
 * Marks the visitor as subscribed and keeps the popup UI in sync.
 */
function markSubscribedAndSyncPopup() {
  const days = getPopupCookieDays();

  markCustomerNewsletterSubscribed(days);
  suppressNewsletterPopup(days);

  const popup = document.querySelector('newsletter-popup-component');
  if (popup instanceof HTMLElement) {
    swapPopupToSuccess(popup);
  }
}

/**
 * Removes Shopify customer-form query params after feedback is applied.
 */
function cleanCustomerFormUrl() {
  const url = new URL(window.location.href);
  const hadFormResponse =
    url.search.includes('customer_posted') || url.search.includes('form_type=customer');

  if (!hadFormResponse) return;

  url.searchParams.delete('customer_posted');
  url.searchParams.delete('form_type');

  for (const key of [...url.searchParams.keys()]) {
    if (key.startsWith('contact[')) {
      url.searchParams.delete(key);
    }
  }

  if (url.hash.startsWith('#FooterUtilities-') || url.hash.startsWith('#NewsletterPopup-')) {
    url.hash = '';
  }

  history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
}

/**
 * @param {string} submittedFormId
 * @returns {'thanks' | 'subscribed' | null}
 */
function getFooterSuccessVariant(submittedFormId) {
  const element = getFormResponseElement(submittedFormId);
  if (!(element instanceof HTMLElement) || !hasCustomerFormRedirect()) return null;
  if (element.hasAttribute('data-customer-form-has-errors')) return null;

  // Server flags are the source of truth when Shopify sets them.
  if (element.hasAttribute('data-customer-form-already-subscribed')) return 'subscribed';
  if (element.hasAttribute('data-customer-form-posted-success')) return 'thanks';

  const search = window.location.search;
  const emailInput = element.querySelector('input[name="contact[email]"]');
  const hasEmail = emailInput instanceof HTMLInputElement && emailInput.value.trim() !== '';

  if (search.includes('customer_posted=true')) return 'thanks';
  if (hasEmail) return 'subscribed';

  return null;
}

/**
 * Applies success/error feedback for the customer newsletter form that was submitted.
 */
export function applyCustomerNewsletterFormResponse() {
  if (responseApplied) return;
  if (!isCustomerFormResponse()) return;

  const submittedFormId = getSubmittedFormId();
  if (!submittedFormId) return;

  responseApplied = true;
  sessionStorage.removeItem(CUSTOMER_NEWSLETTER_SESSION_KEY);

  const cookieDays = getPopupCookieDays();

  if (submittedFormId.startsWith('FooterUtilities-')) {
    const container = document.querySelector(`[data-customer-form-id="${submittedFormId}"]`);

    if (hasFormResponseErrors(submittedFormId)) {
      if (container instanceof HTMLElement) revealFormFeedback(container, 'error');
    } else {
      const variant = getFooterSuccessVariant(submittedFormId);

      if (variant) {
        markSubscribedAndSyncPopup();
        revealAllFooterSuccess(variant, { focus: false, scroll: false });
        cleanCustomerFormUrl();
      }
    }

    suppressNewsletterPopup(cookieDays);
    return;
  }

  if (submittedFormId.startsWith('NewsletterPopup-')) {
    const popup = document.querySelector('newsletter-popup-component');

    if (!(popup instanceof HTMLElement)) return;

    if (hasFormResponseErrors(submittedFormId)) {
      showPopupErrorState(popup);
    } else if (isSuccessfulCustomerPost() || isAlreadySubscribedFormResponse(submittedFormId)) {
      showPopupSuccessState(popup);
      markSubscribedAndSyncPopup();
      cleanCustomerFormUrl();
    }
  }
}

document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;

    if (!(form instanceof HTMLFormElement)) return;

    const formType = form.querySelector('input[name="form_type"]');

    if (!(formType instanceof HTMLInputElement) || formType.value !== 'customer') return;

    rememberSubmittedForm(form);
  },
  true
);

const initCustomerNewsletterForm = () => {
  applyCustomerNewsletterFormResponse();
  syncPopupSubscriptionState(document.querySelector('newsletter-popup-component'));
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCustomerNewsletterForm);
} else {
  initCustomerNewsletterForm();
}
